import areIntlLocalesSupported from 'intl-locales-supported'
import {isNil} from 'lodash'
import moment from 'moment'
import 'moment/min/locales'
import {formatRoundedNumber} from "helpers/financial"

export const ONE_SECOND_MS = 1000
export const ONE_DAY_MILLISECONDS = 86400000

export const MONTHS = {
  JANUARY: 0,
  FEBRUARY: 1,
  MARCH: 2,
  APRIL: 3,
  MAY: 4,
  JUNE: 5,
  JULY: 6,
  AUGUST: 7,
  SEPTEMBER: 8,
  OCTOBER: 9,
  NOVEMBER: 10,
  DECEMBER: 11,
}

export const formatLocaleDate = (date, locale, format) => {
  return moment(date).locale(locale).format(format)
}

export const formatLocaleDateTime = (date, locale, format) => {
  return `${formatLocaleDate(date, locale, format)} ${getTimeString(date)}`
}

// For format options, see: https://momentjs.com/docs/#/displaying/
export const formatDate = (date, format) => {
  return moment(date).format(format).toString()
}

// Form format options, see: https://momentjs.com/docs/#/displaying/
export const parseDate = (dateStr, format) => {
  return moment(dateStr, format).toDate()
}

export const fromNow = (dateMs, now = new Date().getTime()) => {
  return from(dateMs, now)
}

export const from = (dateMs, tillMs) => {
  return moment(dateMs).from(tillMs)
}

export const getDateFormat = () => {
  // Use the native Intl.DateTimeFormat if available, or a polyfill if not.
  if (areIntlLocalesSupported(['en-GB'])) {
    return global.Intl.DateFormat
  } else {
    const IntlPolyfill = require('intl')
    const ret = IntlPolyfill.DateFormat
    require('intl/locale-data/jsonp/en-GB')
    return ret
  }
}

export const getDateTimeFormat = () => {
  // Use the native Intl.DateTimeFormat if available, or a polyfill if not.
  if (areIntlLocalesSupported(['en-GB'])) {
    return global.Intl.DateTimeFormat
  } else {
    const IntlPolyfill = require('intl')
    const ret = IntlPolyfill.DateTimeFormat
    require('intl/locale-data/jsonp/en-GB')
    return ret
  }
}

// Returns a date as a string
export const getDateString = (dateMs, dateFormat = 'L', locale = 'en-gb') => {
  if (locale) moment.locale(locale)
  if (isNil(dateMs)) return ''

  return moment(new Date(dateMs)).format(dateFormat).toString()
}

export const getTimeString = (datetimeMs) => {
  if (isNil(datetimeMs)) return ''
  return new Date(datetimeMs).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
}

export const getDateTimeString = (datetimeMs, dateFormat, locale) => {
  if (isNil(datetimeMs)) return ''
  return getDateString(datetimeMs, dateFormat, locale) + ' ' + getTimeString(datetimeMs)
}

// Returns the time ex: 12:00 AM - 13:30 AM
export const getTimeRangeString = (allDay, startingDate, endingDate) => {
  if (allDay === false) {
    return getTimeString(startingDate) + ' - ' + getTimeString(endingDate)
  } else {
    return '12:00 AM - 12:00 PM'
  }
}

// Returns the time ex:
// 5-Oct 2016 12:00 AM - 13:30 AM
// 5-Oct 2016 12:00 AM - 7-Oct 12:00 AM
// 5-Oct 2016 - 7-Oct 2016
export const getDateTimeRangeString = (allDay, startDate, endDate) => {
  const startDateStr = getDateString(startDate)
  const endDateStr = getDateString(endDate)

  if (allDay) {
    if (startDateStr === endDateStr) return startDateStr
    return startDateStr + ' - ' + endDateStr
  } else {
    const startTimeStr = getTimeString(startDate)
    const endTimeStr = getTimeString(endDate)

    if (startDateStr === endDateStr) return startDateStr + ' ' + startTimeStr + ' - ' + endTimeStr
    return startDateStr + ' ' + startTimeStr + ' - ' + endDateStr + ' ' + endTimeStr
  }
}

const getDateTimeFromUnix = (unixTime) => {
  const convertedDate = new Date()
  convertedDate.setTime(unixTime)
  return convertedDate
}
const getMillisecondsToHours = (milliseconds) => {
  return formatRoundedNumber(milliseconds / (1000 * 60 * 60), 2)
}

const getMidnightOfDate = (date) => {
  return new Date(date).setHours(0, 0, 0, 0)
}

const getMidnightOfTheNextDate = (date) => {
  return new Date(date).setHours(24, 0, 0, 0)
}

export const roundDaysUp = (date, alwaysRollUp = false) => {
  if (isNil(date)) return null
  let day = date.getDate()
  const month = date.getMonth()
  const year = date.getFullYear()
  const hours = date.getHours()
  const mins = date.getMinutes()
  const secs = date.getSeconds()
  const ms = date.getMilliseconds()
  if (alwaysRollUp || hours + mins + secs + ms > 0) day++
  return new Date(year, month, day)
}

export const roundDaysDown = (date) => {
  if (isNil(date)) return null
  const day = date.getDate()
  const month = date.getMonth()
  const year = date.getFullYear()
  return new Date(year, month, day)
}

export const isSameDate = (milliseconds1, milliseconds2) => {
  const date1 = new Date(milliseconds1)
  const date2 = new Date(milliseconds2)
  return (
    date1.getFullYear === date2.getFullYear &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

export const getDaysToDue = (today, dueDate) => {
  return Math.round((dueDate - today) / ONE_DAY_MILLISECONDS)
}

export const getDaysDueLabel = (daysDue, noun, taskName) =>
  daysDue === 0
    ? `${taskName} is ${noun} today`
    : daysDue === 1
      ? `${taskName} is ${noun} tomorrow`
      : `${taskName} is ${noun} in ${daysDue} days`

export const setTimeToMidnight = (timestamp) => {
  return moment(timestamp).startOf('day').valueOf()
}

export const isMidnight = (timestamp) => {
  const date = new Date(timestamp)
  return date.getHours() === 0
    && date.getMinutes() === 0
    && date.getSeconds() === 0
    && date.getMilliseconds() === 0
}

export const getDateWithAppliedTime = (dateWithTimeToApplyMs, dateToApplyTimeToMs) => {
  const timeSource = moment(dateWithTimeToApplyMs)
  const target = moment(dateToApplyTimeToMs).set({
    hour: timeSource.hour(),
    minute: timeSource.minute(),
    second: timeSource.second(),
    millisecond: timeSource.millisecond(),
  })
  return target.valueOf()
}

export default {
  formatDate,
  formatLocaleDate,
  parseDate,
  fromNow,
  getDateFormat,
  getDateTimeFormat,
  getDateString,
  getTimeString,
  getDateTimeString,
  getTimeRangeString,
  getDateTimeRangeString,
  getMidnightOfDate,
  getMidnightOfTheNextDate,
  getDateTimeFromUnix,
  getMillisecondsToHours,
  roundDaysUp,
  roundDaysDown,
}
